/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * RGBARRY-CUSTOMIZATION: route-driven page-view classification (W-23789028 / GTM data layer).
 *
 * WHY THIS EXISTS. The `PageViewTracker` originally decided whether to fire a generic
 * `view_page` by prefix-matching `location.pathname` against a blocklist (`/product`,
 * `/category`, `/search`, `/checkout`). The GTM assessment
 * (`docs/ASSESSMENT-GOOGLE-TAG-MANAGER.md`, "SPA page-view conflict") flags that string
 * matching as fragile, and the legacy-URL work (W-23754187) proved it: PDP URLs became
 * `/<slug>/<id>.html` and PLP URLs became bare slugs, so neither matches `/product` or
 * `/category` any more. The classifier has to key off the RESOLVED ROUTE, not the URL shape.
 *
 * WHAT IT DOES. Each route declares its analytics identity once, via its `handle` export
 * (`handle.analytics.routeType`) — the same `handle`-driven, SSR-safe mechanism
 * `page-ui-config.ts` already uses for `<main>` layout. `useRouteType()` reads the leaf
 * match's declared type through `useMatches()`. Because it's the route, not the URL, that
 * carries the label, this survives any future URL-shape change (and correctly classifies a
 * bare-slug PLP that no path prefix could match).
 *
 * The assessment is explicit (line 175): product / category / search / cart / checkout routes
 * should STILL emit a page view IN ADDITION to their GA4 ecommerce event — the ecommerce event
 * does not replace the page-view lifecycle. So `routeType` is a LABEL carried on the page-view,
 * not a suppression flag.
 */
import { useMatches } from 'react-router';

/**
 * Analytics classification of a route, emitted as `route_type` on the GA4 `page_view`.
 *
 * `'other'` is the default for any route that doesn't declare one (account pages, static
 * marketing pages, etc.) — they still fire a page view, just without a commerce label.
 */
export type RouteType =
    | 'product'
    | 'category'
    | 'search'
    | 'cart'
    | 'checkout'
    | 'order_confirmation'
    | 'content'
    | 'home'
    | 'other';

/**
 * Shape a route module contributes via its `handle` export to declare its analytics identity.
 *
 * @example
 * ```tsx
 * export const handle = {
 *   analytics: { routeType: 'product' },
 * } satisfies PageAnalyticsHandle;
 * ```
 */
export interface PageAnalyticsHandle {
    analytics?: {
        routeType: RouteType;
    };
}

/**
 * Read the leaf route's declared `handle.analytics.routeType`.
 *
 * Backed by `useMatches`, which resolves on the server too, so this is safe to call during
 * render as well as in the page-view effect. Falls back to `'other'` when the active route
 * declares no analytics identity.
 */
export function useRouteType(): RouteType {
    const matches = useMatches();
    const leafMatch = matches[matches.length - 1];
    const handle = leafMatch?.handle as PageAnalyticsHandle | undefined;
    return handle?.analytics?.routeType ?? 'other';
}

// Carry `routeType` on the runtime's `view_page` event without forking the runtime package.
// `ViewPageEvent` is an `interface`, so declaration merging adds an OPTIONAL field: existing
// producers stay valid, and `createEvent('view_page', { path, routeType, payload })` type-checks.
// The GA4 adapter reads it back to emit `route_type` on the `page_view` dataLayer push.
declare module '@salesforce/storefront-next-runtime/events' {
    interface ViewPageEvent {
        routeType?: RouteType;
    }
}
